import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText, PlayCircle, Newspaper, Award } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import {
  ABOUT_LEAD,
  ABOUT_OVERVIEW,
  ABOUT_MISSION,
  ABOUT_HISTORY,
  ABOUT_IMPACT,
  ABOUT_WHO,
  ABOUT_EXPLORE,
  ABOUT_PRESS,
} from "@/content/about";
import { AboutHero } from "@/components/site/about/AboutHero";

/* ---------------- Lead intro ---------------- */
const LeadSection = () => (
  <section aria-label="About World Farmers introduction" className="bg-[var(--wf-paper)] py-12 sm:py-14 lg:py-16">
    <div className="wf-container">
      <Reveal className="mx-auto max-w-3xl">
        <p className="font-serif text-xl leading-8 text-[var(--wf-ink)] sm:text-2xl sm:leading-9">{ABOUT_LEAD}</p>
      </Reveal>
    </div>
  </section>
);

const PRESS_ICON = { pdf: FileText, video: PlayCircle, article: Newspaper };

/* ---------------- Overview ---------------- */
const OverviewSection = () => (
  <section id="about-overview" aria-labelledby="about-overview-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          {ABOUT_OVERVIEW.eyebrow && <Kicker>{ABOUT_OVERVIEW.eyebrow}</Kicker>}
          <h2 id="about-overview-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
            {ABOUT_OVERVIEW.title}
          </h2>
          <div className="mt-5 space-y-4">
            {ABOUT_OVERVIEW.paragraphs.map((p, i) => (
              <p key={i} className="text-base leading-7 text-[var(--wf-soil)]">{p}</p>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <ImageWithCredit
            src={ABOUT_OVERVIEW.image}
            alt={ABOUT_OVERVIEW.imageAlt}
            className="aspect-[4/5] w-full rounded-sm shadow-[var(--wf-shadow)]"
            imgClassName="object-cover"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Mission ---------------- */
const MissionSection = () => (
  <section id="mission" aria-labelledby="mission-heading" className="relative overflow-hidden bg-[var(--wf-farm-green)] py-20 sm:py-24 lg:py-28">
    <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
    <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-[var(--wf-wheat)]/15 blur-3xl" />
    <div className="wf-container relative">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.3fr_1fr] lg:gap-16">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">
            <span aria-hidden="true" className="h-px w-6 bg-[rgba(255,253,248,0.5)]" />
            {ABOUT_MISSION.eyebrow}
          </span>
          <h2 id="mission-heading" className="mt-5 font-serif text-2xl font-semibold leading-[1.25] text-white sm:text-3xl lg:text-[2.35rem] lg:leading-[1.25]">
            {ABOUT_MISSION.statement}
          </h2>
          <p className="mt-6 max-w-[60ch] text-base leading-7 text-[rgba(255,253,248,0.9)]">
            {ABOUT_MISSION.body}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <ImageWithCredit
            src={ABOUT_MISSION.image}
            alt={ABOUT_MISSION.imageAlt}
            className="aspect-square w-full rounded-sm shadow-[var(--wf-shadow)] ring-1 ring-white/20"
            imgClassName="object-cover"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- History ---------------- */
const HistorySection = () => (
  <section id="history" aria-labelledby="about-history-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <ImageWithCredit
            src={ABOUT_HISTORY.image}
            alt={ABOUT_HISTORY.imageAlt}
            className="aspect-[4/3] w-full rounded-sm shadow-[var(--wf-shadow)]"
            imgClassName="object-cover"
          />
        </Reveal>
        <Reveal delay={0.1}>
          {ABOUT_HISTORY.eyebrow && <Kicker>{ABOUT_HISTORY.eyebrow}</Kicker>}
          <h2 id="about-history-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            {ABOUT_HISTORY.title}
          </h2>
          <p className="mt-4 max-w-[54ch] text-base leading-7 text-[var(--wf-soil)]">{ABOUT_HISTORY.body}</p>

          <ol className="mt-8 space-y-0">
            {ABOUT_HISTORY.milestones.map((m, i) => (
              <li key={m.year} className="relative flex gap-4 pb-6 last:pb-0">
                <div className="flex flex-col items-center">
                  <span aria-hidden="true" className="mt-1 inline-flex h-3 w-3 shrink-0 rounded-full bg-[var(--wf-maroon)] ring-4 ring-[var(--wf-maroon)]/15" />
                  {i < ABOUT_HISTORY.milestones.length - 1 && (
                    <span aria-hidden="true" className="mt-1 w-px flex-1 bg-[var(--wf-border)]" />
                  )}
                </div>
                <div className="-mt-0.5 pb-1">
                  <p className="font-serif text-lg font-semibold text-[var(--wf-maroon)]">{m.year}</p>
                  <p className="mt-0.5 text-sm leading-6 text-[var(--wf-soil)]">{m.label}</p>
                </div>
              </li>
            ))}
          </ol>

          <WfButton
            as={Link}
            to={ABOUT_HISTORY.cta.href}
            withArrow
            className="mt-6 px-7"
            data-testid="about-history-cta"
          >
            {ABOUT_HISTORY.cta.label}
          </WfButton>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Impact ---------------- */
const ImpactSection = () => (
  <section id="impact" aria-labelledby="about-impact-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="mx-auto max-w-2xl text-center">
        <Kicker className="justify-center">{ABOUT_IMPACT.eyebrow}</Kicker>
        <h2 id="about-impact-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          {ABOUT_IMPACT.title}
        </h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">{ABOUT_IMPACT.body}</p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
        {ABOUT_IMPACT.stats.map((s, i) => (
          <Reveal key={s.id} delay={0.08 * i}>
            <div data-testid={`about-impact-stat-${s.id}`} className="h-full rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-8 text-center">
              <p className="font-serif text-5xl font-semibold text-[var(--wf-maroon)] sm:text-6xl">{s.value}</p>
              <p className="mx-auto mt-3 max-w-[22ch] text-sm leading-6 text-[var(--wf-soil)]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mx-auto mt-10 flex max-w-3xl items-start gap-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-wheat)] p-6 sm:p-7">
          <span className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--wf-maroon)] text-white">
            <Award className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="text-sm leading-7 text-[var(--wf-ink)] sm:text-base">{ABOUT_IMPACT.highlight}</p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Who We Are overview + CTA ---------------- */
const WhoOverviewSection = () => (
  <section id="who-we-are-overview" aria-labelledby="who-overview-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal delay={0.1} className="order-2 lg:order-1">
          <ImageWithCredit
            src={ABOUT_WHO.image}
            alt={ABOUT_WHO.imageAlt}
            className="aspect-[5/4] w-full rounded-sm shadow-[var(--wf-shadow)]"
            imgClassName="object-cover"
          />
        </Reveal>
        <Reveal className="order-1 lg:order-2">
          <Kicker>{ABOUT_WHO.eyebrow}</Kicker>
          <h2 id="who-overview-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            {ABOUT_WHO.title}
          </h2>
          <p className="mt-4 max-w-[52ch] text-base leading-7 text-[var(--wf-soil)]">{ABOUT_WHO.body}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <WfButton
              as={Link}
              to={ABOUT_WHO.cta.href}
              withArrow
              className="px-7"
              data-testid="about-who-cta"
            >
              {ABOUT_WHO.cta.label}
            </WfButton>
            {ABOUT_WHO.cta2 && (
              <WfButton
                as={Link}
                to={ABOUT_WHO.cta2.href}
                variant="secondary"
                withArrow
                className="px-7"
                data-testid="about-who-cta-2"
              >
                {ABOUT_WHO.cta2.label}
              </WfButton>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Explore hub ---------------- */
const ExploreSection = () => (
  <section id="explore" aria-labelledby="explore-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="mx-auto max-w-2xl text-center">
        <Kicker className="justify-center">{ABOUT_EXPLORE.eyebrow}</Kicker>
        <h2 id="explore-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">
          {ABOUT_EXPLORE.title}
        </h2>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {ABOUT_EXPLORE.links.map((l, i) => (
          <Reveal key={l.id} delay={0.06 * i}>
            <Link
              to={l.href}
              data-testid={`about-explore-${l.id}`}
              className="group flex h-full flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <div className="relative aspect-[5/4] w-full overflow-hidden bg-[var(--wf-cream)]">
                <img
                  src={l.image}
                  alt={l.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[600ms] ease-out motion-safe:group-hover:scale-105"
                />
                <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="flex items-center gap-1.5 font-serif text-lg font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">
                  {l.title}
                  <ArrowRight className="h-4 w-4 opacity-0 transition-[opacity,transform] duration-200 group-hover:translate-x-0.5 group-hover:opacity-100" aria-hidden="true" />
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{l.body}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- In the news ---------------- */
const PressSection = () => {
  const INITIAL = 8;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? ABOUT_PRESS : ABOUT_PRESS.slice(0, INITIAL);
  const hasMore = ABOUT_PRESS.length > INITIAL;

  return (
    <section id="in-the-news" aria-labelledby="press-heading" className="wf-section bg-[var(--wf-cream)]">
      <div className="wf-container">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Kicker className="justify-center">In the news</Kicker>
          <h2 id="press-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            World Farmers in the media
          </h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground">
            Selected coverage of our farmers, programs, and mission from local and national outlets.
          </p>
        </Reveal>

        <ul className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2">
          {visible.map((item, i) => {
            const Icon = PRESS_ICON[item.type] || Newspaper;
            return (
              <li key={`${item.title}-${i}`}>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`about-press-item-${i}`}
                  className="group flex h-full items-start gap-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5 shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-200 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                >
                  <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--wf-green-text)]">
                      {item.publication}
                      <span aria-hidden="true" className="text-[var(--wf-border)]">&middot;</span>
                      <span className="font-medium normal-case tracking-normal text-muted-foreground">{item.date}</span>
                    </span>
                    <span className="mt-1.5 flex items-start gap-1.5 font-serif text-[0.98rem] font-semibold leading-6 text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">
                      {item.title}
                      <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-[var(--wf-maroon)]" aria-hidden="true" />
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>

        {hasMore && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="about-press-toggle"
              aria-expanded={showAll}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--wf-maroon)] px-6 py-3 text-sm font-semibold text-[var(--wf-maroon)] transition-colors duration-200 hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              {showAll ? "Show less" : `Show all coverage (${ABOUT_PRESS.length})`}
            </button>
            <WfButton as={Link} to="/media" variant="secondary" withArrow className="px-6" data-testid="about-press-media-link">
              Full media archive
            </WfButton>
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------------- Page ---------------- */
export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <AboutHero />
        <LeadSection />
        <OverviewSection />
        <MissionSection />
        <HistorySection />
        <WhoOverviewSection />
      </main>
      <SiteFooter />
    </>
  );
}
